import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { auth } from "../auth/firebase";
import "./Login.css";
import "../index.css";
import Footer from "../components/Footer";
import { PageNav } from "../components/PageNav";
import buttonBackground from "../assets/button_background_blue.webp";

export default function PasswordReset() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [oobCode, setOobCode] = useState<string | null>(null);
  const [email, setEmail] = useState<string>("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [isValidCode, setIsValidCode] = useState(false);

  useEffect(() => {
    const code = searchParams.get("oobCode");
    if (!code) {
      setError("Invalid or missing reset code");
      setVerifying(false);
      return;
    }

    setOobCode(code);

    // Verify the password reset code is valid
    verifyPasswordResetCode(auth, code)
      .then((emailAddress) => {
        setEmail(emailAddress);
        setIsValidCode(true);
        setVerifying(false);
      })
      .catch(() => {
        //        console.error("Error verifying reset code:", err);
        setError("Invalid or expired reset link. Please request a new one.");
        setVerifying(false);
      });
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!newPassword) {
      setError("Please enter a new password");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    // Check for password strength requirements
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumber) {
      setError(
        "Password must contain at least one uppercase letter, one lowercase letter, and one number",
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!oobCode) {
      setError("Invalid reset code");
      return;
    }

    setIsLoading(true);

    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      alert(
        "Password has been reset successfully! You can now log in with your new password.",
      );
      navigate("/login");
    } catch (err) {
      //      console.error("Error resetting password:", err);
      if (err instanceof Error) {
        const authError = err as { code?: string; message: string };
        if (authError.code === "auth/weak-password") {
          setError(
            "Password is too weak. Use at least 8 characters with uppercase, lowercase, and numbers",
          );
        } else if (authError.code === "auth/expired-action-code") {
          setError("Reset link has expired. Please request a new one.");
        } else {
          setError("Failed to reset password. Please try again.");
        }
      } else {
        setError("Failed to reset password. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (verifying) {
    return (
      <div>
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-overlay-spinner"></div>
          </div>
        )}
        <PageNav />
        <div className="form-container" style={{ marginTop: 100 }}>
          <div className="form-content">
            <h1 className="font_family_base title">Reset Link...</h1>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!isValidCode) {
    return (
      <div>
        <PageNav />
        <div className="form-container" style={{ marginTop: 100 }}>
          <div className="form-content">
            <h1 className="font_family_base title">Invalid Reset Link</h1>

            <div
              style={{
                minHeight: "48px",
                marginBottom: "20px",
              }}
            >
              {error && (
                <div
                  className="font_family_base"
                  style={{
                    // backgroundColor: "#fee",
                    color: "#FF0000",
                    padding: "12px",
                    borderRadius: "8px",
                    textAlign: "center",
                    border: "1px solid #FF0000",
                    fontWeight: "bold",
                  }}
                >
                  {error}
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <button
                onClick={() => navigate("/login")}
                className="my-form-button-wide button-base button-3d font_family_base"
                style={{
                  marginTop: "20px",
                  marginBottom: "5px",
                  backgroundImage: `url(${buttonBackground})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  color: "white",
                  fontWeight: 700,
                }}
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-overlay-spinner"></div>
        </div>
      )}
      <PageNav />
      <div className="form-container" style={{ marginTop: 100 }}>
        <div className="form-content center_me">
          <h1 className="font_family_base  title header_text">
            Reset Your Password
          </h1>

          <p style={{ marginBottom: 20, color: "#FFF", textAlign: "center" }}>
            Enter a new password for: <strong>{email}</strong>
          </p>

          <div>
            {error && (
              <div
                style={{
                  // backgroundColor: "#fee",
                  color: "#FF0000",
                  padding: "12px",
                  borderRadius: "8px",
                  textAlign: "center",
                  border: "1px solid #FF0000",
                  fontWeight: "bold",
                }}
              >
                {error}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <label className="font_family_base field-name">
              New Password: *
            </label>
            <input
              type="password"
              id="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input"
              style={{ marginBottom: 20 }}
              placeholder="Enter new password"
              disabled={isLoading}
              autoComplete="new-password"
            />

            <label className="font_family_base field-name">
              Confirm New Password: *
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input"
              style={{ marginBottom: 20 }}
              placeholder="Confirm new password"
              disabled={isLoading}
              autoComplete="new-password"
            />

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <button
                type="submit"
                className="my-form-button-wide button-base button-3d font_family_base"
                disabled={isLoading}
                style={{
                  marginTop: "5px",
                  marginBottom: "5px",
                  backgroundImage: `url(${buttonBackground})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  color: "white",
                  fontWeight: 700,
                }}
              >
                {isLoading ? (
                  <div className="loading-spinner"></div>
                ) : (
                  "Reset Password"
                )}
              </button>
            </div>
          </form>

          <button
            onClick={() => navigate("/login")}
            className={`link-text font_family_base ${
              isLoading ? "link-text-disabled" : ""
            }`}
            disabled={isLoading}
            type="button"
            style={{ marginTop: 20 }}
          >
            Back to Login
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
